import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { SystemColors } from 'melp-design/style';
import {
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  IconButton,
  InputAdornment,
  styled,
} from '@mui/material';
import {
  AutoAwesomeOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Button } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { insertIntoInput } from 'utils/insertIntoInput';
import { ContentLanguage } from 'types/general';
import { useTranslateText } from 'store/ai-translate';
import EmojiPicker from '../EmojiPicker/EmojiPicker';

const StyledTextField = styled(MuiTextField)(({ label }) => ({
  width: '100%',
  '& .MuiInputLabel-root': {
    color: SystemColors.grey[55],
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.4,
    transform: 'translate(15px, 15px) scale(1)',

    '&.MuiInputLabel-shrink': {
      transform: 'translate(15px, 8px) scale(0.71)',
    },
    '&.Mui-focused': {
      color: SystemColors.grey[55],
    },
    '&.Mui-error': {
      color: SystemColors.grey[55],
    },
    '& .MuiInputLabel-asterisk': {
      color: 'inherit',
    },
  },
  '& .MuiOutlinedInput-root': {
    '&.MuiInputBase-multiline': {
      padding: !!label ? '22px 15px 8px' : '15px',
    },
    '& .MuiOutlinedInput-input': {
      padding: !!label ? '22px 15px 8px' : '15px',
      color: SystemColors.black,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1.4,
      height: 'auto',

      '&.MuiInputBase-inputMultiline': {
        padding: 0,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 6,

      '& legend': {
        maxWidth: '0.01px',
      },
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: SystemColors.grey[75],
    },
  },
}));

const useSecureText = () => {
  const [isSecure, setIsSecure] = useState(true);

  return {
    InputProps: {
      sx: {
        '-webkit-text-security': isSecure ? 'disc' : 'none',
      },
      autoComplete: 'nope',
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={() => setIsSecure((current) => !current)}
            size="large"
          >
            {isSecure ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    },
  };
};

interface CustomProps {
  value?: string;
  onChange?: (value: string) => void;
  emoji?: boolean;
  secure?: boolean;
  readonly?: boolean;
  rows?: number;
  errorMessage?: string;
  infoMessage?: string;
  ai?: {
    text: string;
    inputLanguage: Uppercase<ContentLanguage>;
    outputLanguage: Uppercase<ContentLanguage>;
  };
}

type Props = Omit<MuiTextFieldProps, keyof CustomProps | 'value' | 'onChange'> &
  CustomProps;

export const TextInput = forwardRef(
  (
    {
      value,
      onChange,
      disabled,
      emoji,
      secure,
      rows,
      readonly,
      errorMessage,
      infoMessage,
      ai,
      ...props
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => innerRef.current);

    const { InputProps: SecureTextInputProps } = useSecureText();

    const { mutate: translateText, isLoading: isTranslating } =
      useTranslateText();

    const isDisabled = isTranslating || disabled;

    return (
      <StyledTextField
        ref={innerRef}
        value={value}
        onChange={({ target }) => onChange?.(target.value)}
        disabled={isDisabled}
        InputProps={{
          ...(secure
            ? SecureTextInputProps
            : {
                endAdornment: (
                  <>
                    {emoji ? (
                      <EmojiPicker
                        disabled={isDisabled}
                        onSelect={(emoji) => {
                          if (innerRef.current) {
                            const [inputEl] =
                              innerRef.current.getElementsByTagName(
                                rows ? 'textarea' : 'input',
                              );
                            insertIntoInput(inputEl, emoji);
                          }
                        }}
                      />
                    ) : null}

                    {ai && ai.inputLanguage !== ai.outputLanguage ? (
                      <Button
                        label={t('ai.actions.translate')}
                        size="sm"
                        variant="secondary"
                        icon={AutoAwesomeOutlined}
                        disabled={isDisabled}
                        onClick={() => {
                          if (onChange) {
                            translateText(ai, {
                              onSuccess: ({ data }) =>
                                onChange(data.translatedText),
                            });
                          }
                        }}
                      />
                    ) : null}
                  </>
                ),
              }),
          readOnly: readonly,
        }}
        InputLabelProps={{ sx: { whiteSpace: 'nowrap' } }}
        FormHelperTextProps={{
          sx: { fontSize: 12, letterSpacing: 'inherit' },
        }}
        {...(rows ? { multiline: true, minRows: rows } : {})}
        error={!!errorMessage}
        helperText={
          errorMessage && infoMessage ? (
            <>
              {errorMessage}
              <br />
              <span style={{ color: SystemColors.grey[45] }}>
                {infoMessage}
              </span>
            </>
          ) : (
            errorMessage || infoMessage || undefined
          )
        }
        {...props}
      />
    );
  },
);

TextInput.displayName = 'TextInput';
